import type { FC, SVGProps } from "react";
import { motion } from "motion/react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import { progressStyle, svgStyle, trackStyle } from "./CircularProgress.css";

const RADIUS = 240;
const circumference = 2 * Math.PI * RADIUS;

export type CircularProgressProps = Pick<SVGProps<SVGSVGElement>, "className"> &
  RecipeVariants<typeof trackStyle> & {
    progress: number;
    strokeWidth?: number;
    absolute?: boolean;
    infinite?: boolean;
  };

/**
 * A circular progress indicator.
 */
export const CircularProgress: FC<CircularProgressProps> = ({
  progress,
  className,
  strokeWidth = 10,
  variant = "default",
  absolute = false,
  infinite = false,
  ...props
}) => {
  const progressOffset = circumference - (progress / 100) * circumference;

  return (
    <motion.svg
      className={mergeClasses(svgStyle({ absolute }), className)}
      viewBox={`0 0 ${RADIUS + strokeWidth} ${RADIUS + strokeWidth}`}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        rotate: infinite ? 360 : 0,
      }}
      exit={{ opacity: 0 }}
      transition={
        infinite
          ? {
              rotate: {
                duration: 1.5,
                repeat: Infinity,
                ease: "linear",
              },
              opacity: {
                duration: 0.3,
              },
            }
          : {
              opacity: {
                duration: 0.3,
              },
            }
      }
      {...props}
    >
      <circle
        className={trackStyle({ variant })}
        cx={RADIUS / 2 + strokeWidth / 2}
        cy={RADIUS / 2 + strokeWidth / 2}
        r={RADIUS / 2}
        fill="none"
        strokeWidth={strokeWidth}
      />
      <motion.circle
        className={progressStyle({ variant })}
        cx={RADIUS / 2 + 5}
        cy={RADIUS / 2 + 5}
        r={RADIUS / 2}
        fill="none"
        strokeWidth={strokeWidth}
        strokeDasharray={
          infinite
            ? `${circumference * 0.25} ${circumference * 0.75}`
            : `${circumference} ${circumference}`
        }
        strokeDashoffset={infinite ? 0 : progressOffset}
        initial={{ strokeDashoffset: infinite ? 0 : circumference }}
        animate={{ strokeDashoffset: infinite ? 0 : progressOffset }}
        transition={{ duration: infinite ? 0 : 1 }}
        transform={`rotate(-90 ${RADIUS / 2 + 5} ${RADIUS / 2 + 5})`}
      />
    </motion.svg>
  );
};
